import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Button } from '@optools/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@optools/components/ui/dialog';
import { UPDATE_SESSION_OVERRIDE } from '@optools/services/operator-gateway/overrides/session/mutations';
import { SessionOverride } from '@optools/services/operator-gateway/overrides/session/types';
import { ClientType, MatchParams } from '@optools/services/operator-gateway/overrides/types';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { SessionOverrideForm, SessionOverrideFormValues } from './session-override-form';

export interface UpdateSessionOverrideProps {
  override: SessionOverride | null;
}

export function UpdateSessionOverride({ override }: UpdateSessionOverrideProps) {
  const [open, setOpen] = useState(false);

  const [updateSessionOverride, { loading }] = useMutation(UPDATE_SESSION_OVERRIDE, {
    onCompleted: ({ updateSessionOverride }) => {
      if (!updateSessionOverride) {
        toast.error('Update Override Error', {
          description: 'Failed to update override, please try again',
          duration: Infinity,
        });

        return;
      } else {
        toast.success('Session Override successfully updated');
      }

      setOpen(false);
    },
  });

  const handleClose = (nextOpen: boolean) => {
    if (loading) return;
    setOpen(nextOpen);
  };

  const handleSubmit = (values: SessionOverrideFormValues) => {
    if (!override) return;
    const matchParams: MatchParams = {};
    const { clientType, features, userId } = values;
    const id = +override.id;

    if (clientType !== ClientType.ALL_CLIENTS) matchParams.clientType = clientType;
    if (userId) matchParams.userId = userId;

    updateSessionOverride({
      update: (cache, { data }) => {
        if (!data?.updateSessionOverride) return;

        cache.writeFragment({
          data: { features, id, matchParams },
          fragment: gql`
            fragment SessionOverride on ExperienceOverrideSearchView {
              id
              features
              matchParams
            }
          `,
          id: cache.identify({ __typename: 'ExperienceOverrideSearchView', id: override.id }),
        });
      },
      variables: { features, id, matchParams },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <Button size="icon" title="Update Override" variant="outline">
          <Pencil />
          <span className="sr-only">Update Override</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Update Override</DialogTitle>
          <DialogDescription className="sr-only">Update an existing override</DialogDescription>
        </DialogHeader>
        <SessionOverrideForm loading={loading} override={override} onSubmit={handleSubmit} />
      </DialogContent>
    </Dialog>
  );
}
